/**
 * FormInputBase - Abstract base class for form input components
 *
 * Provides shared validation, event handling, and state management for:
 * - wf-input
 * - wf-email
 * - wf-textarea
 * - wf-field
 *
 * Subclasses must implement:
 * - _setupValidators(): Configure validators based on component properties
 * - _getValidationTriggerProperties(): Return property names that trigger validator rebuild
 * - render(): Render the component-specific template
 *
 * Subclasses may override:
 * - _getDebounceMs(): Return debounce delay for input validation (default: 600ms)
 * - _getFocusableElement(): Return the focusable element (default: 'input')
 */

import { LitElement, CSSResultGroup, html, nothing, TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';

import { formInputBaseStyles } from './form-input-base.styles.js';
import type { Validator } from '../../types.js';

/**
 * Default debounce delay for input validation (in milliseconds)
 */
const DEFAULT_DEBOUNCE_MS = 600;

/**
 * Abstract base class for form input components
 */
export abstract class FormInputBase extends LitElement {
  /**
   * Base styles for all form input components
   * Subclasses can extend by providing their own static styles array
   */
  static styles: CSSResultGroup = formInputBaseStyles;

  // ============================================
  // Common Properties
  // ============================================

  /**
   * Field name for form data
   */
  @property({ type: String })
  name = '';

  /**
   * Label text
   */
  @property({ type: String })
  label = '';

  /**
   * Whether field is required
   */
  @property({ type: Boolean })
  required = false;

  /**
   * Hint text displayed below the input
   */
  @property({ type: String })
  hint = '';

  // ============================================
  // Internal State
  // ============================================

  /**
   * Validation error message (displayed to user)
   */
  @state()
  protected _errorMessage = '';

  /**
   * Current field value
   */
  @state()
  protected _value = '';

  /**
   * Whether validation has been activated (after first validation attempt)
   * When true, input changes trigger debounced validation
   */
  protected _validationActivated = false;

  /**
   * Debounce timer for input validation
   */
  protected _debounceTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Array of validators for this field
   */
  protected _validators: Validator[] = [];

  // ============================================
  // Lifecycle Methods
  // ============================================

  connectedCallback(): void {
    super.connectedCallback();
    this._setupValidators();
  }

  updated(changedProperties: Map<string, unknown>): void {
    const triggerProps = this._getValidationTriggerProperties();
    const shouldRebuild = triggerProps.some((prop) => changedProperties.has(prop));
    if (shouldRebuild) {
      this._setupValidators();
    }
  }

  // ============================================
  // Abstract Methods (must be implemented by subclasses)
  // ============================================

  /**
   * Setup validators based on component properties.
   * Called on connectedCallback and when validation-triggering properties change.
   */
  protected abstract _setupValidators(): void;

  /**
   * Return property names that should trigger validator rebuild when changed.
   * At minimum, should include 'required'.
   */
  protected abstract _getValidationTriggerProperties(): string[];

  // ============================================
  // Overridable Methods
  // ============================================

  /**
   * Return the debounce delay for input validation (in milliseconds).
   * Override in subclasses that need different debounce timing.
   */
  protected _getDebounceMs(): number {
    return DEFAULT_DEBOUNCE_MS;
  }

  /**
   * Return the CSS selector for the focusable element.
   * Override in subclasses with different focusable elements (e.g., 'textarea').
   */
  protected _getFocusableSelector(): string {
    return 'input';
  }

  // ============================================
  // Shared Render Helpers
  // ============================================

  /**
   * Render the label element
   */
  protected _renderLabel(): TemplateResult | typeof nothing {
    if (!this.label) {return nothing;}
    return html`<label
      class="wf-label ${this.required ? 'wf-label-required' : ''}"
      for="${this.name}"
      data-testid="wf-label"
    >${this.label}</label>`;
  }

  /**
   * Render the hint text (only shown when no error)
   */
  protected _renderHint(): TemplateResult | typeof nothing {
    if (!this.hint || this._errorMessage) {return nothing;}
    return html`<span class="wf-hint">${this.hint}</span>`;
  }

  /**
   * Render the error message container with animation support
   */
  protected _renderError(): TemplateResult {
    return html`<div class="wf-error-wrapper ${this._errorMessage ? 'wf-has-error' : ''}">
      <span class="wf-error-message" role="alert" aria-live="polite" data-testid="wf-error">
        ${this._errorMessage || ''}
      </span>
    </div>`;
  }

  // ============================================
  // Event Handlers
  // ============================================

  /**
   * Handle input events with debounced validation
   */
  protected _handleInput = (e: Event): void => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    this._value = target.value;

    // Debounced validation after activation
    if (this._validationActivated) {
      if (this._debounceTimer) {
        clearTimeout(this._debounceTimer);
      }
      this._debounceTimer = setTimeout(() => {
        this.validate();
      }, this._getDebounceMs());
    }

    // Dispatch change event
    this.dispatchEvent(
      new CustomEvent('wf-change', {
        detail: {
          name: this.name,
          value: this._value,
        },
        bubbles: true,
        composed: true,
      })
    );
  };

  /**
   * Handle blur events
   */
  protected _handleBlur = (): void => {
    this.dispatchEvent(
      new CustomEvent('wf-blur', {
        detail: { name: this.name, value: this._value },
        bubbles: true,
        composed: true,
      })
    );
  };

  /**
   * Handle focus events
   */
  protected _handleFocus = (): void => {
    this.dispatchEvent(
      new CustomEvent('wf-focus', {
        detail: { name: this.name },
        bubbles: true,
        composed: true,
      })
    );
  };

  // ============================================
  // Public API
  // ============================================

  /**
   * Get current value
   */
  get value(): string {
    return this._value;
  }

  /**
   * Set value programmatically
   */
  set value(val: string) {
    this._value = val;
  }

  /**
   * Add a custom validator
   */
  addValidator(validator: Validator): void {
    this._validators.push(validator);
  }

  /**
   * Validate the field asynchronously
   * @returns Promise resolving to true if valid, false otherwise
   */
  async validate(): Promise<boolean> {
    // Activate validation mode (enables debounced input validation)
    this._validationActivated = true;

    // Use getter to allow subclasses to customize value retrieval
    const currentValue = this.value;

    for (const validator of this._validators) {
      try {
        const result = await validator.validate(currentValue);
        if (!result.valid) {
          this._errorMessage = result.error ?? validator.message ?? 'Validation failed';
          return false;
        }
      } catch (error) {
        console.error(`[${this.tagName}] Validator error:`, error);
        this._errorMessage = 'Validation error occurred';
        return false;
      }
    }

    this._errorMessage = '';
    return true;
  }

  /**
   * Check if field is valid synchronously (doesn't update UI)
   */
  get isValid(): boolean {
    // Use getter to allow subclasses to customize value retrieval
    const currentValue = this.value;

    for (const validator of this._validators) {
      const result = validator.validate(currentValue);
      if (result instanceof Promise) {
        console.warn(`[${this.tagName}] Async validator called synchronously`);
        continue;
      }
      if (!result.valid) {
        return false;
      }
    }
    return true;
  }

  /**
   * Show an error message manually
   */
  showError(message: string): void {
    this._errorMessage = message;
  }

  /**
   * Clear the error message
   */
  clearError(): void {
    this._errorMessage = '';
  }

  /**
   * Focus the input element
   */
  focus(): void {
    const element = this.shadowRoot?.querySelector(this._getFocusableSelector());
    (element as HTMLElement | null)?.focus();
  }

  /**
   * Reset field to initial state
   */
  reset(): void {
    this._value = '';
    this._errorMessage = '';
    this._validationActivated = false;
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
  }
}
